import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Select } from '@nyce/admin-kit/ui';
import { db, isActive, type DbServerRef, type Job, type JobKind } from '../../api/db';
import { EmptyState, ErrorBox, Loading, TableScroller } from '../../components/common';
import { elapsedMs, formatDateTime, formatDuration, formatRelative } from '../../lib/format';
import { dbErrorText, StatusChip, Target, useInterval, useNow } from './shared';

const PAGE = 50;

/** Merge a fresh first page into the loaded list: the fresh rows win, older loaded pages are kept. */
export function mergeJobs(fresh: Job[], loaded: Job[]): Job[] {
  if (!fresh.length) return loaded;
  const minFresh = Math.min(...fresh.map((j) => j.id));
  return [...fresh, ...loaded.filter((j) => j.id < minFresh)];
}

export function JobsTab({
  servers,
  onOpen,
  refreshKey,
}: {
  servers: DbServerRef[];
  onOpen: (id: number) => void;
  /** Bumped by the page when a job changed elsewhere (drawer, a new job). */
  refreshKey: number;
}) {
  const [kind, setKind] = useState<JobKind | ''>('');
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [more, setMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  /** true once "Load older" was used (the first-page refresh must not reset `more`). */
  const jobsLoaded = useRef(false);

  const refresh = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    try {
      const page = await db.jobs.list({ limit: PAGE, kind: kind || undefined });
      if (id !== seq.current) return;
      setJobs((cur) => (cur ? mergeJobs(page, cur) : page));
      setMore((m) => (jobsLoaded.current ? m : page.length >= PAGE));
      setError(null);
    } catch (e) {
      if (id === seq.current) setError(dbErrorText(e));
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    setJobs(null);
    setMore(true);
    jobsLoaded.current = false;
  }, [kind]);
  useEffect(() => {
    void refresh();
  }, [refresh, refreshKey]);

  const anyActive = !!jobs?.some((j) => isActive(j.status));
  useInterval(() => void refresh(), 5000, anyActive);
  const now = useNow(1000, anyActive);

  const older = async () => {
    if (!jobs?.length) return;
    setLoading(true);
    try {
      const before = Math.min(...jobs.map((j) => j.id));
      const page = await db.jobs.list({ limit: PAGE, before, kind: kind || undefined });
      jobsLoaded.current = true;
      setJobs((cur) => [...(cur ?? []), ...page.filter((j) => j.id < before)]);
      setMore(page.length >= PAGE);
    } catch (e) {
      setError(dbErrorText(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="dbx-toolbar">
        <Select
          aria-label="Kind"
          value={kind}
          onChange={(v) => setKind(v as JobKind | '')}
          options={[
            { value: '', label: 'All kinds' },
            { value: 'backup', label: 'Backups' },
            { value: 'copy', label: 'Copies' },
          ]}
        />
        <Button size="sm" variant="ghost" onClick={() => void refresh()}>
          Refresh
        </Button>
        {anyActive && <span className="dbx-muted">refreshing every 5 s while jobs run</span>}
      </div>
      <ErrorBox error={error} />
      <Loading loading={loading && !jobs} />
      {jobs && !jobs.length && <EmptyState card title="No jobs yet" />}
      {!!jobs?.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards dbx-jobs" data-testid="db-jobs">
              <thead>
                <tr>
                  <th className="nyk-ui-num">#</th>
                  <th>Status</th>
                  <th>Kind</th>
                  <th>Database</th>
                  <th>Created</th>
                  <th className="nyk-ui-num">Duration</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const d = elapsedMs(j.startedAt, j.finishedAt, now);
                  return (
                    <tr
                      key={j.id}
                      data-job={j.id}
                      className="dbx-clickable"
                      tabIndex={0}
                      onClick={() => onOpen(j.id)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(j.id)}
                    >
                      <td className="nyk-ui-num nyk-card-title">#{j.id}</td>
                      <td data-label="Status">
                        <StatusChip job={j} />
                      </td>
                      <td data-label="Kind">
                        {j.kind}
                        {j.scheduleId && <div className="nyk-ui-hint">scheduled</div>}
                      </td>
                      <td data-label="Database">
                        <Target servers={servers} server={j.serverId} database={j.database} />
                        {j.targetServerId && (
                          <>
                            {' → '}
                            <Target
                              servers={servers}
                              server={j.targetServerId}
                              database={j.targetDatabase ?? ''}
                            />
                          </>
                        )}
                        {j.error && (
                          <div className="dbx-err-text dbx-clip" title={j.error}>
                            {j.error}
                          </div>
                        )}
                      </td>
                      <td data-label="Created" title={formatDateTime(j.createdAt)}>
                        {formatRelative(j.createdAt, now)}
                      </td>
                      <td className="nyk-ui-num" data-label="Duration">
                        {d == null ? '—' : formatDuration(d)}
                      </td>
                      <td data-label="By">{j.createdBy}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {!!jobs?.length && more && (
        <div className="nyk-pager">
          <Button variant="secondary" size="sm" onClick={() => void older()} loading={loading}>
            Load older
          </Button>
        </div>
      )}
    </>
  );
}
